import {computed, ref} from 'vue';
import axios from 'axios';
import {MaintenanceApi} from '@/common/api/MaintenanceApi';
import {IMaintenanceCurrent, IMaintenanceLockout, MaintenancePhase} from '@/types/components/maintenance/IMaintenance';

// The single tenant that stays reachable during an active maintenance window.
// Must match MAINTENANCE_ADMIN_TENANT_URL on the backend, otherwise the FE
// would either show the lockout on the admin tenant or skip it on a customer
// tenant — both are wrong. There's no other bypass: working through the
// admin subdomain IS the bypass.
export const MAINTENANCE_ADMIN_TENANT_URL = 'https://app.community-coaching.com';

const phase = ref<MaintenancePhase>('none');
const startsAt = ref<string>('');
const endsAt = ref<string>('');
const showBannerFrom = ref<string>('');
const message = ref<string>('');

const isAdminTenantFE = (): boolean => {
  // Dev mode: treat as admin so local development is never blocked by a
  // production-style lockout. Production: compare actual origin.
  if (import.meta.env.DEV) {
    return true;
  }
  return window.location.origin === MAINTENANCE_ADMIN_TENANT_URL;
};

// True only when the running window should fully block the UI. Drives both
// the SignIn lockout text and the MaintenanceScreen fullscreen takeover.
const maintenance = computed<boolean>(() =>
  phase.value === 'active' && !isAdminTenantFE()
);

// Banner stays in the top bar whenever a window is announced; the planning
// brief specified "always show in the top bar" for scheduled state.
const bannerVisible = computed<boolean>(() => phase.value === 'scheduled');

const applyFromCurrent = (payload: IMaintenanceCurrent): void => {
  phase.value = payload.phase;
  startsAt.value = payload.starts_at ?? '';
  endsAt.value = payload.ends_at ?? '';
  message.value = payload.message ?? '';
  showBannerFrom.value = payload.show_banner_from ?? '';
};

const applyLockout = (payload: IMaintenanceLockout): void => {
  // Hard cut from a 503 interceptor — we don't get show_banner_from / starts_at
  // back, but we don't need them in active mode.
  phase.value = 'active';
  endsAt.value = payload.ends_at;
  message.value = payload.message;
};

const loadMaintenance = async (): Promise<void> => {
  try {
    const response = await axios.get<IMaintenanceCurrent>(MaintenanceApi.current(), {withCredentials: false});
    applyFromCurrent(response.data);
  } catch {
    // Soft-fail: if the public endpoint is unreachable, default to no
    // maintenance rather than locking users out of a healthy system.
    phase.value = 'none';
  }
};

export {
  phase,
  startsAt,
  endsAt,
  showBannerFrom,
  message,
  maintenance,
  bannerVisible,
  isAdminTenantFE,
  loadMaintenance,
  applyFromCurrent,
  applyLockout,
};
